import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BottomTabBar } from '@/components/ui/BottomTabBar';
import { useAuthStore } from '@/store/authStore';
import { useData } from '@/hooks/useData';
import { COUNTRIES } from '@/constants/education';
import { AUTHORITIES, getSchoolYears } from '@/constants/education';
import { apiFetch } from '@/services/apiGateway';
import { GlobalHeader } from '@/components/GlobalHeader';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';
import { useUpdateStudentAcademicContext } from '@/services/students';
import { AcademicYearResetModal } from '@/components/AcademicYearResetModal';
import { markTimetableRequired } from '@/utils/academicReset';

interface ProfileResponse {
  child_id: string;
  display_name: string | null;
  mode: 'high_school' | 'university' | null;
  school_year: string | null;
  country: string | null;
  education_authority: string | null;
  education_authorities: string[] | null;
  date_of_birth: string | null;
}

interface TimetableEntry {
  subject: string;
  day: string;
  start_time: string;
  end_time: string;
  room: string | null;
}

interface TimetableResponse {
  entries: TimetableEntry[];
}

function calcAge(dob: string | null): number | null {
  if (!dob) return null;
  const parts = dob.split('-').map(Number);
  const y = parts[0] ?? 0;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const today = new Date();
  let age = today.getFullYear() - y;
  if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age--;
  return age;
}

function formatExpiry(iso: string): string {
  const datePart = iso.split('T')[0] ?? iso;
  const parts = datePart.split('-').map(Number);
  const y = parts[0] ?? 0;
  const mo = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const date = new Date(y, mo - 1, d);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function StudentMorePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { childId } = useAuthStore();
  const signOut = useAuthStore((s) => s.signOut);

  const { data: profile, isLoading, refetch } = useData<ProfileResponse>(
    childId ? `/children/${childId}/profile` : null,
  );

  const { data: timetableData } = useData<TimetableResponse>(
    childId ? `/children/${childId}/timetable` : null,
  );
  const subjects = Array.from(
    new Set((timetableData?.entries ?? []).map((e) => e.subject)),
  ).sort();

  // ── Study again state ────────────────────────────────────────────────────────
  const [studySubject, setStudySubject] = useState('');
  const [studyTheme, setStudyTheme] = useState('');

  // ── Profile edit state ──────────────────────────────────────────────────────
  // Editing country or level of study (university students only) also triggers
  // the destructive academic-year reset (US-PRF-01/US-PRF-02) — wipes timetable/
  // free-time/commitments — so those two fields are confirmed via
  // AcademicYearResetModal before saving; editing authority alone saves
  // immediately, no confirmation.
  const [isEditing, setIsEditing] = useState(false);
  const [country, setCountry] = useState('');
  const [editStudyLevel, setEditStudyLevel] = useState('');
  const [selectedAuthorities, setSelectedAuthorities] = useState<string[]>([]);
  const [authorityFilter, setAuthorityFilter] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const updateAcademicContext = useUpdateStudentAcademicContext(childId ?? '');

  useEffect(() => {
    if (profile) {
      const c = profile.country ?? 'FR';
      const saved = profile.education_authorities ?? [];
      const legacy = profile.education_authority;
      const initial = saved.length > 0 ? saved : legacy ? [legacy] : [AUTHORITIES[c]?.[0]?.code ?? 'MEN'];
      setCountry(c);
      setEditStudyLevel(profile.school_year ?? '');
      setSelectedAuthorities(initial);
    }
  }, [profile]);

  const handleEditCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    setEditStudyLevel('');
    setAuthorityFilter('');
    const firstCode = AUTHORITIES[newCountry]?.[0]?.code ?? '';
    setSelectedAuthorities(firstCode ? [firstCode] : []);
  };

  const toggleAuthority = (code: string) => {
    setSelectedAuthorities((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const isUniversity = profile?.mode === 'university';
  const academicContextChanged =
    isUniversity &&
    (country !== (profile?.country ?? 'FR') || editStudyLevel !== (profile?.school_year ?? ''));

  const handleSaveProfile = async () => {
    if (!childId) return;
    if (academicContextChanged) {
      setShowResetModal(true);
      return;
    }
    setIsSaving(true);
    try {
      await apiFetch(`/children/${childId}/profile`, {
        method: 'PUT',
        body: JSON.stringify({
          country,
          education_authorities: selectedAuthorities.length > 0 ? selectedAuthorities : undefined,
        }),
      });
      setIsEditing(false);
      refetch();
    } catch {
      // 403 means age < 15 restriction — silently revert
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmAcademicReset = async () => {
    if (!childId) return;
    try {
      await updateAcademicContext.mutateAsync({
        country_code: country,
        study_level: editStudyLevel,
        education_authorities: selectedAuthorities,
      });
      markTimetableRequired(childId);
      setShowResetModal(false);
      navigate('/student/timetable/scan');
    } catch {
      setShowResetModal(false);
    }
  };

  // ── University: invite parent ───────────────────────────────────────────────
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [inviteExpiry, setInviteExpiry] = useState<string | null>(null);
  const [isGeneratingInvite, setIsGeneratingInvite] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);

  const handleGenerateInvite = async () => {
    if (!childId) return;
    setIsGeneratingInvite(true);
    try {
      const res = await apiFetch<{ invite_code: string; expires_at: string }>(
        `/students/${childId}/invite-parent`,
        { method: 'POST' },
      );
      setInviteCode(res.invite_code);
      setInviteExpiry(res.expires_at);
      setInviteCopied(false);
    } finally {
      setIsGeneratingInvite(false);
    }
  };

  const handleCopyInvite = async () => {
    if (!inviteCode) return;
    try {
      await navigator.clipboard.writeText(inviteCode);
    } catch {
      const el = document.createElement('textarea');
      el.value = inviteCode;
      el.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2000);
  };

  // ── Notification preferences (university students only) ───────────────────
  const [notifEnabled, setNotifEnabled] = useState(true);
  const notifSaving = useRef(false);

  const toggleNotifications = async () => {
    if (notifSaving.current) return;
    notifSaving.current = true;
    const next = !notifEnabled;
    setNotifEnabled(next);
    try {
      await apiFetch('/notifications/preferences', {
        method: 'POST',
        body: JSON.stringify({ enabled: next }),
      });
    } catch {
      setNotifEnabled(!next);
    } finally {
      notifSaving.current = false;
    }
  };

  // ── Sign out ───────────────────────────────────────────────────────────────
  const handleSignOut = async () => {
    await signOut();
    localStorage.removeItem('studywize_access_token');
    navigate('/login', { replace: true });
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const age = calcAge(profile?.date_of_birth ?? null);
  const canEdit = isUniversity || age === null || age >= 15;

  const inviteDisplay = inviteCode
    ? `${inviteCode.slice(0, 4)} ${inviteCode.slice(4)}`
    : null;

  const academicStudyLevels = getSchoolYears(country, 'university');

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader />
        <div className="app-content" style={{ paddingBottom: '24px' }}>

          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 24px' }}>
            {t('student_more.settings_profile')}
          </h1>

          {/* ── Profile card ── */}
          {isLoading ? (
            <div className="loading-skeleton" style={{ height: '140px', borderRadius: '12px', marginBottom: '24px' }} />
          ) : (
            <div className="card" style={{ marginBottom: '24px' }}>
              <p style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 2px' }}>
                {profile?.display_name ?? t('student_more.default_name')}
              </p>
              {profile?.school_year && (
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '0 0 16px' }}>
                  {profile.school_year}
                </p>
              )}

              {!isEditing ? (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-dark)' }}>
                      <span style={{ color: 'var(--text-muted)', marginRight: '6px' }}>{t('student_more.country')}:</span>
                      {profile?.country ?? '—'}
                    </p>
                    {!isUniversity && (
                      <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-dark)' }}>
                        <span style={{ color: 'var(--text-muted)', marginRight: '6px' }}>{t('student_more.authority')}:</span>
                        {(() => {
                          const auths = profile?.education_authorities ?? (profile?.education_authority ? [profile.education_authority] : []);
                          if (auths.length === 0) return '—';
                          const countryCode = profile?.country ?? 'FR';
                          return auths.map((code) => AUTHORITIES[countryCode]?.find((a) => a.code === code)?.name ?? code).join(', ');
                        })()}
                      </p>
                    )}
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => setIsEditing(true)}
                      style={{ width: '100%', fontSize: '0.9rem' }}
                    >
                      ✏️ {t('student_more.edit_details')}
                    </button>
                  )}
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                        {t('student_more.country')}
                      </label>
                      <select
                        className="input"
                        value={country}
                        onChange={(e) => handleEditCountryChange(e.target.value)}
                      >
                        <option value="" disabled>{t('student_more.select_country')}</option>
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                        ))}
                      </select>
                    </div>
                    {isUniversity && (
                      <div>
                        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                          {t('academic_context.study_level')}
                        </label>
                        <select
                          className="input"
                          value={editStudyLevel}
                          onChange={(e) => setEditStudyLevel(e.target.value)}
                        >
                          <option value="" disabled>{t('academic_context.select_study_level')}</option>
                          {academicStudyLevels.map((level) => (
                            <option key={level} value={level}>{level}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {!isUniversity && (() => {
                      const allAuths = AUTHORITIES[country] ?? [];
                      const filteredAuths = authorityFilter.trim()
                        ? allAuths.filter((a) =>
                            a.name.toLowerCase().includes(authorityFilter.toLowerCase()) ||
                            a.code.toLowerCase().includes(authorityFilter.toLowerCase()),
                          )
                        : allAuths;
                      return allAuths.length > 0 ? (
                        <div>
                          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                            {t('student_more.authority')}
                          </label>
                          {allAuths.length > 4 && (
                            <input
                              type="text"
                              className="input"
                              value={authorityFilter}
                              onChange={(e) => setAuthorityFilter(e.target.value)}
                              placeholder={t('actions.filter_authorities')}
                              style={{ marginBottom: '6px', fontSize: '0.85rem' }}
                            />
                          )}
                          <div style={{ border: '1px solid var(--border)', borderRadius: '8px', maxHeight: '160px', overflowY: 'auto' }}>
                            {filteredAuths.map((a) => (
                              <label key={a.code} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedAuthorities.includes(a.code)}
                                  onChange={() => toggleAuthority(a.code)}
                                  style={{ width: '16px', height: '16px' }}
                                />
                                <span style={{ fontSize: '0.85rem', color: 'var(--text-dark)' }}>{a.name}</span>
                              </label>
                            ))}
                          </div>
                          {selectedAuthorities.length > 0 && (
                            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                              {t('add_child.selected', { count: selectedAuthorities.length })}
                            </p>
                          )}
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => {
                        setIsEditing(false);
                        const c = profile?.country ?? 'FR';
                        const saved = profile?.education_authorities ?? [];
                        const legacy = profile?.education_authority;
                        setCountry(c);
                        setEditStudyLevel(profile?.school_year ?? '');
                        setSelectedAuthorities(saved.length > 0 ? saved : legacy ? [legacy] : []);
                        setAuthorityFilter('');
                      }}
                      style={{ flex: 1 }}
                    >
                      {t('actions.cancel')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSaveProfile}
                      disabled={isSaving || (isUniversity && !editStudyLevel)}
                      style={{ flex: 1 }}
                    >
                      {isSaving ? t('actions.saving') : t('student_more.save_changes')}
                    </button>
                  </div>
                </>
              )}

              {/* ── Application language ── */}
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
                <LanguageSwitcher label={t('settings.app_language')} />
              </div>
            </div>
          )}

          <AcademicYearResetModal
            open={showResetModal}
            isSaving={updateAcademicContext.isPending}
            onCancel={() => setShowResetModal(false)}
            onConfirm={handleConfirmAcademicReset}
          />

          {/* ── Study again ── */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 16px' }}>
              {t('student_more.study_again')}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Subject */}
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                  {t('student_more.subject')}
                </label>
                {subjects.length > 0 ? (
                  <select
                    className="input"
                    value={studySubject}
                    onChange={(e) => setStudySubject(e.target.value)}
                  >
                    <option value="">{t('student_more.select_subject')}…</option>
                    {subjects.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="input"
                    placeholder={t('student_more.subject_placeholder')}
                    value={studySubject}
                    onChange={(e) => setStudySubject(e.target.value)}
                  />
                )}
              </div>

              {/* Theme */}
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                  {t('student_more.theme')}
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder={t('student_more.theme_placeholder')}
                  value={studyTheme}
                  onChange={(e) => setStudyTheme(e.target.value)}
                />
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!studySubject.trim()}
                  onClick={() => navigate(
                    `/student/quiz?subject=${encodeURIComponent(studySubject)}&theme=${encodeURIComponent(studyTheme)}`,
                  )}
                  style={{ width: '100%' }}
                >
                  📝 {t('session_complete.start_quiz')}
                </button>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    disabled={!studySubject.trim()}
                    onClick={() => navigate(
                      `/student/explain?subject=${encodeURIComponent(studySubject)}&theme=${encodeURIComponent(studyTheme)}`,
                    )}
                    style={{ flex: 1 }}
                  >
                    💬 {t('student_more.explain_this')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    disabled={!studySubject.trim()}
                    onClick={() => navigate(
                      `/student/flashcards?subject=${encodeURIComponent(studySubject)}&theme=${encodeURIComponent(studyTheme)}`,
                    )}
                    style={{ flex: 1 }}
                  >
                    🎴 {t('student_more.new_flashcards')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── Revision plan ── */}
          <Link to="/student/revision-plan" style={{ textDecoration: 'none' }}>
            <div className="card" style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '1.5rem' }}>📋</span>
                <div>
                  <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-dark)', fontSize: '0.95rem' }}>{t('student_more.revision_plan')}</p>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>{t('student_more.revision_plan_desc')}</p>
                </div>
              </div>
              <span style={{ color: 'var(--text-muted)', fontSize: '1.2rem' }}>›</span>
            </div>
          </Link>

          {/* ── Subscription — university students only ── */}
          {isUniversity && (
            <Link to="/student/subscription" style={{ textDecoration: 'none' }}>
              <div className="card" style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '1.5rem' }}>💳</span>
                  <div>
                    <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-dark)', fontSize: '0.95rem' }}>{t('student_subscription.title')}</p>
                  </div>
                </div>
                <span style={{ color: 'var(--text-muted)', fontSize: '1.2rem' }}>›</span>
              </div>
            </Link>
          )}

          {/* ── Family Privacy card — university students only ── */}
          {!isLoading && isUniversity && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 6px' }}>
                {t('student_more.family_privacy')}
              </h2>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '0 0 16px', lineHeight: 1.5 }}>
                {t('student_more.invite_desc')}
              </p>

              {!inviteCode ? (
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleGenerateInvite}
                  disabled={isGeneratingInvite}
                  style={{ width: '100%' }}
                >
                  {isGeneratingInvite ? t('actions.generating') : t('student_more.generate_invite')}
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{
                    background: 'var(--primary-light)', borderRadius: '10px',
                    padding: '20px', textAlign: 'center',
                  }}>
                    <p style={{
                      fontFamily: 'monospace', fontSize: '2rem', fontWeight: 700,
                      letterSpacing: '0.2em', color: 'var(--primary)',
                      margin: '0 0 6px', userSelect: 'none',
                    }}>
                      {inviteDisplay}
                    </p>
                    {inviteExpiry && (
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>
                        {t('student_more.valid_until', { date: formatExpiry(inviteExpiry) })}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={handleCopyInvite}
                    style={{ width: '100%' }}
                  >
                    {inviteCopied ? t('actions.copied') : t('actions.copy')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={handleGenerateInvite}
                    disabled={isGeneratingInvite}
                    style={{ width: '100%', fontSize: '0.85rem' }}
                  >
                    {t('student_more.generate_new_code')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Notifications card ── */}
          {!isLoading && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 6px' }}>
                {t('notifications.study_reminders')}
              </h2>
              {isUniversity ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    {t('student_more.push_reminders_desc')}
                  </p>
                  <button
                    type="button"
                    onClick={toggleNotifications}
                    aria-label={notifEnabled ? t('student_more.disable_notifications') : t('student_more.enable_notifications')}
                    style={{
                      marginLeft: '16px', width: '46px', height: '26px', borderRadius: '13px',
                      border: 'none', background: notifEnabled ? 'var(--primary, #4F46E5)' : '#D1D5DB',
                      position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: '3px',
                      left: notifEnabled ? '23px' : '3px',
                      width: '20px', height: '20px', borderRadius: '50%',
                      background: 'white', transition: 'left 0.2s',
                    }} />
                  </button>
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  {t('student_more.notif_parent')}
                </p>
              )}
            </div>
          )}

          {/* ── General Info ── */}
          <div className="card" style={{ marginBottom: '24px', padding: 0, overflow: 'hidden' }}>
            <p style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', padding: '12px 20px 8px' }}>
              {t('general_info.title')}
            </p>
            <a
              href={`${import.meta.env.VITE_WEBSITE_URL ?? 'https://vadostudy.com'}?view=guide&lang=${i18n.resolvedLanguage}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid var(--border)', color: 'var(--text-dark)', textDecoration: 'none', fontSize: '0.9rem' }}
            >
              <span>📖 {t('general_info.user_guide')}</span>
              <span style={{ color: 'var(--text-muted)' }}>↗</span>
            </a>
            <a
              href={import.meta.env.VITE_WEBSITE_URL ?? 'https://vadostudy.com'}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid var(--border)', color: 'var(--text-dark)', textDecoration: 'none', fontSize: '0.9rem' }}
            >
              <span>🌐 {t('general_info.website')}</span>
              <span style={{ color: 'var(--text-muted)' }}>↗</span>
            </a>
          </div>

          {/* ── Sign out ── */}
          <button
            type="button"
            onClick={handleSignOut}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--danger)', fontWeight: 600, fontSize: '1rem',
              padding: '16px 0', textAlign: 'left', width: '100%',
            }}
          >
            🚪 {t('actions.signout')}
          </button>

        </div>

        <BottomTabBar activeTab="More" />
      </div>
    </div>
  );
}
